import { useEffect, useMemo, useState } from "react";
import {
    TablePagination,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import newRequest from "../../utils/newRequest";
import "./Taccount.scss";
import ExpandableComponent from "../expandableComponent/ExpandableComponent";
import { motion } from "framer-motion";
import numeral from "numeral";
import { MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import OnScreenReports from "../onScreenReports/OnScreenReports";
import CircularButton from "../ui/CustomButton";
import { useSelector } from "react-redux";
import ExportToExcelButton from "../../components/exportToExcelButton/ExportToExcelButton.jsx";
import moment from "moment";
import InitialsAvatar from "../InitialsAvatar/InitialsAvatar";


const Taccount = ({ originalDocno, originalDocType }) => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const DEFAULT_LOCAL_CUR = currentUser?.currency_code_xx;
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [taccount, setTaccount] = useState(null);
    const [taccountLines, setTaccountLines] = useState([]);
    const [currentContributionPage, setCurrentContributionPage] = useState(0);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
    const [reportGroupArray] = useState(["contribution"]);

    const exportConfig = [
        { key: "trn_doc_no", label: "Document No.", format: "s" },
        { key: "trn_doc_type", label: "Document Type", format: "s" },
        { key: "trn_biz_unit", label: "Branch", format: "s" },
        { key: "fmt_gl_date_xx", label: "GL Date", format: "d" },
        { key: "trn_doc_year", label: "FY Year", format: "s" },
        { key: "trn_doc_period", label: "FY Period", format: "s" },
        { key: "trn_cur_code", label: "Currency", format: "s" },
        // { key: "trn_amount_fc", label: "Transaction Amount", format: "n" },
        // { key: "trn_amount_lc", label: "Converted Amount", format: "n" },
        { key: "trn_cur_rate", label: "Rate", format: "n" },
        { key: "trn_drcr_flag", label: "Flag", format: "s" },

        { key: "debit_fc_amount_xx", label: "Debit FC", format: "n" },
        { key: "debit_lc_amount_xx", label: "Debit LC", format: "n" },
        { key: "credit_fc_amount_xx", label: "Credit FC", format: "n" },
        { key: "credit_lc_amount_xx", label: "Credit LC", format: "n" },

        { key: "trn_mgl_code", label: "Main Ledger Code", format: "s" },
        { key: "trn_mgl_code_xx", label: "Main Ledger Name", format: "s" },
        { key: "trn_mgl_subtype_xx", label: "Sub Type", format: "s" },
        { key: "trn_sgl_code", label: "Sub Ledger Code", format: "s" },
        { key: "trn_sgl_code_xx", label: "Sub Ledger Name", format: "s" },
        { key: "coa_level_category", label: "Category", format: "s" },

        { key: "trn_direction", label: "Direction", format: "s" },
        { key: "trn_original_docno", label: "Original Doc", format: "s" },
        { key: "trn_ent_code", label: "Entity", format: "s" },

        { key: "trn_policy_no", label: "Policy No", format: "s" },
        { key: "trn_endorsement_no", label: "Endorsement No", format: "s" },
        { key: "trn_product_code", label: "Product", format: "s" },
        { key: "trn_mc_code", label: "Class", format: "s" },
        { key: "trn_sc_code", label: "Sub Class", format: "s" },
        { key: "trn_cc_code", label: "Cover", format: "s" },
        { key: "trn_insured_code", label: "Insured", format: "s" },
        { key: "trn_intermediary_code", label: "Intermediary", format: "s" },
        { key: "trn_treaty_code", label: "Treaty", format: "s" },
        { key: "trn_claim_no", label: "Claim No", format: "s" },
        { key: "trn_narration", label: "Narration", format: "s" },
    ];

    const fetchTaccount = async () => {
        try {
            const res = await newRequest.post("/gl/getTaccount", {
                original_doctype: originalDocType,
                original_docno: originalDocno,
            });
            // logData("data", res.data);
            setTaccount(res.data);
            setTaccountLines(Array.isArray(res.data?.transactions) ? res.data.transactions : []);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchTaccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originalDocno, originalDocType]);

    const summary = taccount?.summary || {};
    const docCur = (summary?.trn_cur_code || "").toUpperCase();
    const isForeign = !!docCur && docCur !== DEFAULT_LOCAL_CUR;

    const handleContributionPageChange = (_event, newPage) => {
        setCurrentContributionPage(newPage);
    };

    const handleContributionRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentContributionPage(0);
    };

    // totals (fallback to compute if backend doesn't provide lc totals)
    const computedTotals = useMemo(() => {
        const lines = Array.isArray(taccountLines) ? taccountLines : [];
        const totalDebitFc = lines.filter((x) => x?.trn_drcr_flag === "D").reduce((s, x) => s + (Number(x?.trn_amount_fc) || 0), 0);
        const totalCreditFc = lines.filter((x) => x?.trn_drcr_flag === "C").reduce((s, x) => s + (Number(x?.trn_amount_fc) || 0), 0);
        const totalDebitLc = lines.filter((x) => x?.trn_drcr_flag === "D").reduce((s, x) => s + (Number(x?.trn_amount_lc) || 0), 0);
        const totalCreditLc = lines.filter((x) => x?.trn_drcr_flag === "C").reduce((s, x) => s + (Number(x?.trn_amount_lc) || 0), 0);
        return { totalDebitFc, totalCreditFc, totalDebitLc, totalCreditLc };
    }, [taccountLines]);

    const totalDebitFc = Number(summary?.total_debit_fc ?? computedTotals.totalDebitFc);
    const totalCreditFc = Number(summary?.total_credit_fc ?? computedTotals.totalCreditFc);
    const totalDebitLc = Number(summary?.total_debit_lc ?? computedTotals.totalDebitLc);
    const totalCreditLc = Number(summary?.total_credit_lc ?? computedTotals.totalCreditLc);

    const paginatedLines = useMemo(() => {
        const lines = Array.isArray(taccountLines) ? taccountLines : [];
        const start = currentContributionPage * rowsPerPage;
        return lines.slice(start, start + rowsPerPage);
    }, [taccountLines, currentContributionPage, rowsPerPage]);

    const money = (n) => numeral(Number(n || 0)).format("0,0.00");

    const AmountCell = ({ main, sub, mainCur, showSub }) => (
        <div className="tacc-amt">
            <div className="tacc-amt__main">
                <span className="tacc-amt__num">{money(main)}</span>
                <span className="tacc-amt__cur">{mainCur}</span>
            </div>
            {showSub && (
                <div className="tacc-amt__main">
                    <span className="tacc-amt__num">{money(sub)}</span>
                    <span className="tacc-amt__cur">{DEFAULT_LOCAL_CUR}</span>
                </div>
            )}
        </div>
    );

    return (
        <motion.div
            className="taccount"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {taccountLines?.length > 0 && (
                <ExpandableComponent
                    title={t("glEntries").toUpperCase()}
                    subtitle={
                        <div className="tacc-subtitle">
                            {/* <h1 className="sub-title">{summary?.trn_doc_type}</h1> */}
                            <h1 className="page-title">{summary?.trn_doc_no}</h1>
                        </div>
                    }
                    defaultExpanded={true}
                >
                    <div className="taccount-wrapper">
                        <div className="tacc-top">
                            <div className="tacc-top__left">
                                <div className="tacc-kicker">{`${t("summary").toUpperCase()}${docCur ? ` [${docCur}]` : ""}`}</div>

                                <div className="tacc-top__meta">
                                    <div className="tacc-lines">
                                        <span className="tacc-lines__big">{summary?.no_of_lines || taccountLines?.length || 0}</span>
                                        <span className="tacc-lines__label">{(summary?.no_of_lines || taccountLines?.length || 0) === 1 ? t("line").toUpperCase() : t("lines").toUpperCase()}</span>
                                    </div>
                                    <div className="tacc-date">
                                        <span className="tacc-date__label">{t("date").toUpperCase()}</span>
                                        <span className="tacc-date__val">{`${summary?.formatted_gl_date || ""} - [${summary?.trn_doc_period || ""}]`}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="tacc-userCard">
                                <div className="tacc-userCard__avatar">
                                    {summary?.updated_by_photo_url_xx ? (
                                        <img src={summary?.updated_by_photo_url_xx} alt="" />
                                    ) : (
                                        <InitialsAvatar
                                            name={summary?.updated_by_name_xx || ""}
                                            size={27}
                                            currentTheme={currentTheme}
                                        />
                                    )}
                                </div>
                                <div className="tacc-userCard__info">
                                    <div className="tacc-userCard__name">{summary?.updated_by_name_xx || ""}</div>
                                    <div className="tacc-userCard__time">{summary?.time_ago_xx || ""}</div>
                                </div>
                            </div>
                        </div>

                        <div className="table-content tacc-tableWrap">
                            <TableContainer component={Paper} sx={{ border: "none", boxShadow: "none" }} className="table-container">
                                <Table sx={{ width: "100%", border: "none" }} className="tacc-table">
                                    <TableHead className="tablehead">
                                        <TableRow className="tablerow">
                                            <TableCell className="table-cell center tacc-col-num">#</TableCell>
                                            <TableCell className="table-cell left tacc-col-doc">
                                                <span>{t("doc").toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left tacc-col-desc">
                                                <span>{t("desc").toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell center tacc-col-flag">
                                                <span>{t("flag").toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell right tacc-col-money">
                                                <span>{t("debit").toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell right tacc-col-money">
                                                <span>{t("credit").toUpperCase()}</span>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody className="tablebody">
                                        {paginatedLines?.map((row, index) => {
                                            const debitFc = row?.trn_drcr_flag === "D" ? row?.trn_amount_fc : 0;
                                            const creditFc = row?.trn_drcr_flag === "C" ? row?.trn_amount_fc : 0;
                                            const debitLc = row?.trn_drcr_flag === "D" ? row?.trn_amount_lc : 0;
                                            const creditLc = row?.trn_drcr_flag === "C" ? row?.trn_amount_lc : 0;

                                            return (
                                                <TableRow
                                                    key={row._id}
                                                    className={`${selectedTransaction?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                    onClick={() => setSelectedTransaction(row)}
                                                >
                                                    <TableCell className="table-cell center tacc-col-num">
                                                        <span className="sub-title xrsmall">{currentContributionPage * rowsPerPage + index + 1}</span>
                                                    </TableCell>

                                                    <TableCell className="table-cell left tacc-col-doc">
                                                        <span className="sub-title xrsmall">{row?.trn_doc_no}</span>
                                                    </TableCell>

                                                    <TableCell className="table-cell left tacc-col-desc">
                                                        <div className="tacc-desc">
                                                            <span className="tacc-desc__title">{`${row?.trn_mgl_code} - ${row?.trn_mgl_code_xx}`}</span>

                                                            {row?.trn_ent_code && (
                                                                <span className="tacc-desc__sub">{`${row?.trn_ent_code} - ${row?.trn_ent_code_xx}`}</span>
                                                            )}

                                                            {row?.trn_mgl_subtype_xx === "Account" && (
                                                                <span className="tacc-desc__sub">{`${row?.trn_sgl_code} - ${row?.trn_sgl_code_xx}`}</span>
                                                            )}

                                                            {row?.trn_narration && <span className="tacc-desc__note">{row?.trn_narration}</span>}
                                                        </div>
                                                    </TableCell>

                                                    <TableCell className="table-cell center tacc-col-flag">
                                                        <span className={`tacc-flag ${row?.trn_drcr_flag === "D" ? "d" : "c"}`}>
                                                            {row?.trn_drcr_flag}
                                                        </span>
                                                    </TableCell>

                                                    <TableCell className="table-cell right tacc-col-money">
                                                        <AmountCell
                                                            main={debitFc}
                                                            sub={debitLc}
                                                            mainCur={docCur || DEFAULT_LOCAL_CUR}
                                                            showSub={isForeign}
                                                        />
                                                    </TableCell>

                                                    <TableCell className="table-cell right tacc-col-money">
                                                        <AmountCell
                                                            main={creditFc}
                                                            sub={creditLc}
                                                            mainCur={docCur || DEFAULT_LOCAL_CUR}
                                                            showSub={isForeign}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {/* Totals row */}
                                        <TableRow
                                            className={`${selectedTransaction?._id === "lastRow" ? "tablerow totals current" : "tablerow totals"}`}
                                            onClick={() => setSelectedTransaction({ _id: "lastRow" })}
                                        >
                                            <TableCell className="table-cell center tacc-col-num">{""}</TableCell>
                                            <TableCell className="table-cell left tacc-col-doc">{""}</TableCell>

                                            <TableCell className="table-cell left tacc-col-desc">
                                                <span className="tacc-totalsLabel">{t("totals").toUpperCase()}</span>
                                            </TableCell>

                                            <TableCell className="table-cell center tacc-col-flag">{""}</TableCell>

                                            <TableCell className="table-cell right tacc-col-money">
                                                <div className="tacc-totalsAmt">
                                                    <div className="tacc-totalsAmt__main">
                                                        <span className="tacc-totalsAmt__num">{money(totalDebitFc)}</span>
                                                        <span className="tacc-totalsAmt__cur">{docCur || DEFAULT_LOCAL_CUR}</span>
                                                    </div>
                                                    {isForeign && (
                                                        <div className="tacc-totalsAmt__sub">
                                                            <span className="tacc-totalsAmt__num">{money(totalDebitLc)}</span>
                                                            <span className="tacc-totalsAmt__cur">{DEFAULT_LOCAL_CUR}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>

                                            <TableCell className="table-cell right tacc-col-money">
                                                <div className="tacc-totalsAmt">
                                                    <div className="tacc-totalsAmt__main">
                                                        <span className="tacc-totalsAmt__num">{money(totalCreditFc)}</span>
                                                        <span className="tacc-totalsAmt__cur">{docCur || DEFAULT_LOCAL_CUR}</span>
                                                    </div>
                                                    {isForeign && (
                                                        <div className="tacc-totalsAmt__sub">
                                                            <span className="tacc-totalsAmt__num">{money(totalCreditLc)}</span>
                                                            <span className="tacc-totalsAmt__cur">{DEFAULT_LOCAL_CUR}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>

                                {taccountLines?.length > rowsPerPage && (
                                    <TablePagination
                                        rowsPerPageOptions={[10]}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleContributionRowsPerPage}
                                        component="div"
                                        count={taccountLines?.length}
                                        page={currentContributionPage}
                                        onPageChange={handleContributionPageChange}
                                    />
                                )}
                            </TableContainer>

                            {/* Footer: show ONLY for foreign currency */}

                            <div className="tacc-footer">
                                {isForeign ?
                                    <div className="tacc-footer__left">
                                        <span className="tacc-footer__item">
                                            Converted at 1 {docCur} = {money(summary?.trn_cur_rate || 0)} {DEFAULT_LOCAL_CUR}
                                        </span>
                                        <span className="tacc-footer__dot">•</span>
                                        <span className="tacc-footer__item">Rate date: {moment(summary?.trn_gl_date).format("Dd-MMM-YYYY") || ""}</span>
                                    </div>
                                    :
                                    <div className="tacc-footer__left"></div>
                                }

                                <div className="tacc-footer__right">
                                    <button className="tacc-footer__btn" type="button">
                                        Print PDF
                                    </button>
                                    <ExportToExcelButton
                                        data={taccountLines}
                                        exportConfig={exportConfig}
                                        fileName={`${summary?.trn_doc_no} TAccount.xlsx` || "TAccount.xlsx"}
                                        currentTheme={currentTheme}
                                    >
                                        {({ exportToExcel, isExporting }) => (
                                            <button
                                                className="tacc-footer__btn"
                                                type="button"
                                                onClick={exportToExcel}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? "Exporting..." : "Export Excel"}
                                            </button>
                                        )}
                                    </ExportToExcelButton>

                                </div>
                            </div>

                        </div>
                    </div>
                </ExpandableComponent>
            )}

            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t("reports")}</h1>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => setReportsDialogOpen(false)}
                                icon={<MdClose />}
                                width={"35px"}
                                height={"35px"}
                            />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={reportGroupArray}
                                documentData={{
                                    docNo: originalDocno,
                                    docType: "Taccount",
                                    docId: originalDocno,
                                }}
                            />
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default Taccount;
